import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, Database, RefreshCw, Shield, X, Settings, Zap } from 'lucide-react';
import clsx from 'clsx';
import { useRole } from '../../contexts/RoleContext';
import type { Permission, UserRole } from '../../api/client';

interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  permission?: Permission;
  superadminOnly?: boolean;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, permission: 'dashboard:view' },
  { name: 'Leads', href: '/leads', icon: Users, permission: 'leads:view' },
  { name: 'Data Sources', href: '/sources', icon: Database, permission: 'sources:view' },
  { name: 'Automation', href: '/automation', icon: Zap, permission: 'automation:view' },
  { name: 'Admin', href: '/admin', icon: Settings, superadminOnly: true },
];

const roleLabels: Record<UserRole, string> = {
  superadmin: 'Super Admin',
  sales_manager: 'Sales Manager',
  exec: 'Executive',
  sales: 'Sales',
  asst: 'Assistant',
  viewer: 'Viewer',
  unauthorized: 'Unauthorized',
};

export function Sidebar() {
  const { can, canImpersonate, impersonatedRole, setImpersonatedRole, availableRoles, role, actualRole } = useRole();

  // Filter navigation items based on permissions
  const visibleNavigation = navigation.filter((item) => {
    // Check superadmin-only items (use actualRole to keep visible during impersonation)
    if (item.superadminOnly) {
      return actualRole === 'superadmin';
    }
    // Check permission-based items
    if (!item.permission) return true;
    return can(item.permission);
  });

  return (
    <aside className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white border-r border-card-border">
      {/* Impersonation panel for superadmin */}
      {canImpersonate && (
        <div className="p-4 border-b border-card-border bg-yellow-50">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Role Preview</span>
          </div>
          {impersonatedRole ? (
            <div className="flex items-center justify-between bg-yellow-100 rounded-lg px-3 py-2">
              <span className="text-sm font-medium text-yellow-900">
                Viewing as: {roleLabels[impersonatedRole]}
              </span>
              <button
                onClick={() => setImpersonatedRole(null)}
                className="p-1 hover:bg-yellow-200 rounded"
                title="Stop impersonating"
              >
                <X className="w-4 h-4 text-yellow-700" />
              </button>
            </div>
          ) : (
            <select
              value=""
              onChange={(e) => setImpersonatedRole(e.target.value as UserRole)}
              className="w-full text-sm border border-yellow-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">Select role to preview...</option>
              {availableRoles
                .filter((r) => r !== 'superadmin' && r !== 'unauthorized')
                .map((r) => (
                  <option key={r} value={r}>
                    {roleLabels[r]}
                  </option>
                ))}
            </select>
          )}
        </div>
      )}

      <nav className="p-4 space-y-1">
        {visibleNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              clsx(
                'flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-colors',
                isActive
                  ? 'bg-brand-primary text-white'
                  : 'text-font-secondary hover:bg-brand-sand hover:text-brand-primary'
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span>{item.name}</span>
          </NavLink>
        ))}
      </nav>

      {/* Current role indicator */}
      <div className="px-4 py-2">
        <div className="text-xs text-font-secondary">
          Role: <span className="font-medium">{role ? roleLabels[role] : 'Loading...'}</span>
        </div>
      </div>

      {/* Quick actions - only show if user can fetch sources */}
      {can('sources:fetch') && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-card-border">
          <button className="flex items-center justify-center space-x-2 w-full px-4 py-2 bg-brand-secondary text-brand-primary rounded-lg font-medium hover:bg-opacity-90 transition-colors">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh Data</span>
          </button>
        </div>
      )}
    </aside>
  );
}
